import { useState } from 'react';
import { useOrderStatistics } from '@/hooks/useOrder';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useGetEmployeeRoleIsSeller } from '@/hooks/useEmployee';
import { Button } from '@/components/ui/button';
import { Filter, ShoppingCart, DollarSign, Package } from 'lucide-react';
import LoadingCircle from '@/components/LoadingCircle';
import ReactECharts from 'echarts-for-react';
import {
  numberFormat,
  currencyFormat,
  getOrderGroupedBarOption,
} from './ChartUtils';

interface OrderStatisticsProps {
  selectedYear: number;
  onYearChange: (year: number) => void;
}

const OrderStatistics = ({
  selectedYear,
  onYearChange,
}: OrderStatisticsProps) => {
  const [employeeId, setEmployeeId] = useState<string | undefined>('');

  // Lấy data filter
  const { data: employeesData } = useGetEmployeeRoleIsSeller(false);

  // Monthly statistics data
  const { data: orderMonthlyData, isLoading: orderLoading } =
    useOrderStatistics(
      employeeId ? Number(employeeId) : undefined,
      selectedYear
    );

  // Xoá lọc
  const handleClearEmployeeFilter = () => {
    setEmployeeId('');
  };

  return (
    <div className="bg-gradient-to-br from-white to-luxury-50/30 rounded-xl shadow-lg border border-luxury-200/50 overflow-hidden w-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 p-4 text-white">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-white/20 rounded-lg">
            <ShoppingCart className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold">
              Thống kê đơn hàng theo nhân viên
            </h2>
            <p className="text-orange-100 text-xs">
              Phân tích hiệu suất bán hàng và doanh thu
            </p>
          </div>
        </div>
      </div>

      {/* Employee Filter */}
      <div className="p-4 border-b border-luxury-200/50 bg-gradient-to-r from-luxury-50/50 to-white">
        <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3">
          <div className="flex-1 min-w-[200px] w-full sm:w-auto">
            <Select value={employeeId || ''} onValueChange={setEmployeeId}>
              <SelectTrigger className="w-full luxury-input bg-white/80 backdrop-blur-sm border-luxury-300 hover:border-luxury-400 transition-all duration-200 h-9">
                <SelectValue placeholder="Chọn nhân viên" />
              </SelectTrigger>
              <SelectContent className="border-luxury-200 bg-white/95 backdrop-blur-sm">
                {employeesData
                  ?.filter((e) => e.status === 'active')
                  .map((e) => (
                    <SelectItem
                      key={e.id}
                      value={e.id}
                      className="hover:bg-luxury-50"
                    >
                      <span className="font-medium">NV-{e.id}</span> - {e.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[100px]">
            <Select
              value={selectedYear.toString()}
              onValueChange={(value) => onYearChange(Number(value))}
            >
              <SelectTrigger className="w-full luxury-input bg-white/80 backdrop-blur-sm border-luxury-300 hover:border-luxury-400 transition-all duration-200 h-9">
                <SelectValue placeholder="Năm" />
              </SelectTrigger>
              <SelectContent className="border-luxury-200 bg-white/95 backdrop-blur-sm">
                {Array.from(
                  { length: 5 },
                  (_, i) => new Date().getFullYear() - i
                ).map((year) => (
                  <SelectItem
                    key={year}
                    value={year.toString()}
                    className="hover:bg-luxury-50"
                  >
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="luxury-button-outline bg-white/80 backdrop-blur-sm hover:bg-white transition-all duration-200 border-luxury-300 hover:border-luxury-400 h-9"
            onClick={handleClearEmployeeFilter}
          >
            <Filter className="w-3.5 h-3.5 mr-1.5" />
            Xoá lọc
          </Button>
        </div>
      </div>

      {/* Order Overview */}
      {orderMonthlyData && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 p-3 sm:p-4">
          <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-lg p-4 flex flex-col items-center border border-orange-200/50 shadow-md hover:shadow-lg transition-all duration-300 group">
            <div className="p-2 bg-orange-500/10 rounded-full mb-2 group-hover:scale-110 transition-transform duration-200">
              <Package className="w-5 h-5 text-orange-600" />
            </div>
            <span className="text-gray-600 text-xs font-medium mb-1">
              Tổng đơn hàng
            </span>
            <span className="text-2xl font-bold text-orange-700 group-hover:text-orange-800 transition-colors">
              {numberFormat(orderMonthlyData.totalOrders)}
            </span>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-red-100/50 rounded-lg p-4 flex flex-col items-center border border-red-200/50 shadow-md hover:shadow-lg transition-all duration-300 group">
            <div className="p-2 bg-red-500/10 rounded-full mb-2 group-hover:scale-110 transition-transform duration-200">
              <DollarSign className="w-5 h-5 text-red-600" />
            </div>
            <span className="text-gray-600 text-xs font-medium mb-1">
              Tổng doanh thu
            </span>
            <span className="text-2xl font-bold text-red-600 group-hover:text-red-700 transition-colors">
              {currencyFormat(orderMonthlyData.totalAmount)}
            </span>
          </div>
        </div>
      )}

      {/* Order Chart */}
      <div className="p-4">
        {orderLoading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingCircle />
          </div>
        ) : !employeeId ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <div className="p-3 bg-luxury-50 rounded-full mb-3">
              <ShoppingCart className="w-6 h-6 text-luxury-400" />
            </div>
            <p className="text-base font-medium">
              Vui lòng chọn nhân viên để xem thống kê đơn hàng
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Chọn một nhân viên từ danh sách trên để hiển thị biểu đồ
            </p>
          </div>
        ) : orderMonthlyData && orderMonthlyData.monthlyData ? (
          <div className="bg-white/50 backdrop-blur-sm rounded-lg p-3 border border-luxury-200/50">
            <div className="h-[320px]">
              <ReactECharts
                option={getOrderGroupedBarOption(orderMonthlyData.monthlyData)}
                style={{ height: 320 }}
                notMerge={true}
                lazyUpdate={true}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 text-red-500">
            <div className="p-3 bg-red-50 rounded-full mb-3">
              <ShoppingCart className="w-6 h-6 text-red-400" />
            </div>
            <p className="text-base font-medium">Không có dữ liệu</p>
            <p className="text-xs text-red-400 mt-1">
              Không tìm thấy dữ liệu thống kê đơn hàng cho nhân viên này
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderStatistics;
